import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStore } from '@core/store/auth/auth.store';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, filter, from, switchMap, take, throwError } from 'rxjs';

// Flag per controllare se il refresh è già in corso
let isRefreshing = false;

/**
 * Gate per le richieste in coda durante il refresh.
 * - Emette `null` mentre il refresh è in corso (le richieste aspettano)
 * - Emette il nuovo token quando il refresh è completato (le richieste vengono sbloccate)
 */
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/** Clona la richiesta aggiungendo i token/header di autenticazione */
function addAuthHeaders(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'X-Client-Id': sessionStorage.getItem('idCliente') ?? '',
      'X-Cantiere-Id': req.headers.get('X-Cantiere-Id') ?? sessionStorage.getItem('idCantiere') ?? '',
    },
  });
}

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const messageService = inject(MessageService);

  // Evita di intercettare la richiesta di refresh stessa per non generare un loop
  if (req.url.includes('/auth/refresh')) {
    return next(req);
  }

  // Evita di intercettare la richiesta degli assets
  if (req.url.includes('/assets')) {
    return next(req);
  }

  const token = authStore.accessToken();
  const authReq = token ? addAuthHeaders(req, token) : req;

  return next(authReq).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse && error.status === 401)) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        /**
         * Refresh già in corso: metti in coda questa richiesta.
         * Aspetta che `refreshTokenSubject` emetta un token valido (≠ null),
         * poi ritenta la richiesta originale con il nuovo token.
         */
        return refreshTokenSubject.pipe(
          filter((t) => t !== null),
          take(1),
          switchMap((newToken) => next(addAuthHeaders(req, newToken!))),
        );
      }

      // — Avvia il refresh —
      isRefreshing = true;
      refreshTokenSubject.next(null); // Blocca le richieste in coda

      return from(authStore.refresh()).pipe(
        switchMap(() => {
          const newToken = authStore.accessToken();

          if (!newToken) {
            isRefreshing = false;
            authStore.logout();
            return throwError(() => error);
          }

          isRefreshing = false;
          refreshTokenSubject.next(newToken); // Sblocca le richieste in coda
          return next(addAuthHeaders(req, newToken));
        }),
        catchError((refreshErr) => {
          isRefreshing = false;
          refreshTokenSubject.next(null);

          if (refreshErr instanceof HttpErrorResponse && refreshErr.status === 401) {
            messageService.add({
              severity: 'warn',
              summary: 'Sessione Scaduta',
              detail: "Effettua nuovamente l'accesso.",
            });
            authStore.logout();
          }

          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
